/**
 * Toolbar overlay for the pixel world canvas.
 * Shows the pan/zoom hint and an enter-full-screen control (also bound to "F").
 */

import { useEffect } from "react";
import { Maximize2 } from "lucide-react";
import { useWorldStore } from "../../stores/world.js";

function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || node.isContentEditable;
}

export function PixelWorldToolbar() {
  const fullscreen = useWorldStore((s) => s.fullscreen);
  const toggleFullscreen = useWorldStore((s) => s.toggleFullscreen);

  // "F" toggles full-screen (ignored while typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "f" && !e.metaKey && !e.ctrlKey && !isTypingTarget(e.target)) {
        toggleFullscreen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleFullscreen]);

  if (fullscreen) return null;

  return (
    <div className="absolute bottom-4 right-4 z-canvas-ui flex items-center gap-2">
      <div className="glass rounded-lg px-3 py-1.5">
        <span className="text-xs text-text-secondary">Scroll to zoom · Drag to pan</span>
      </div>
      <button
        onClick={() => useWorldStore.getState().setFullscreen(true)}
        title="Full screen (F)"
        className="glass flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-text-secondary transition-[color,scale] duration-200 hover:text-text-primary active:scale-[0.96]"
      >
        <Maximize2 className="h-3.5 w-3.5" />
        <span>Full screen</span>
      </button>
    </div>
  );
}
